import React from 'react';
import { motion } from 'framer-motion';
import { ExternalLinkIcon } from 'lucide-react';
type AppStatus = 'Live' | 'In Progress' | 'Coming Soon';
interface AppCardProps {
  icon: string;
  iconUrl?: string;
  name: string;
  description: string;
  status: AppStatus;
  accentColor: 'forest' | 'flame' | 'amber';
  link?: string;
  videoUrl?: string;
  tags?: string[];
}
const statusStyles: Record<AppStatus, string> = {
  Live: 'bg-forest-light/20 text-forest-light border-forest-light/30',
  'In Progress': 'bg-flame/20 text-flame border-flame/30',
  'Coming Soon': 'bg-parchment/20 text-parchment border-parchment/30'
};
const accentGlows: Record<string, string> = {
  forest: 'hover:shadow-glow-forest hover:border-forest-light/50',
  flame: 'hover:shadow-glow-flame hover:border-flame/50',
  amber: 'hover:shadow-glow-amber hover:border-amber/50'
};
export function AppCard({
  icon,
  iconUrl,
  name,
  description,
  status,
  accentColor,
  link,
  videoUrl,
  tags
}: AppCardProps) {
  return <motion.div whileHover={{
    y: -4,
    scale: 1.02
  }} transition={{
    type: 'spring',
    stiffness: 300,
    damping: 20
  }} className={`
        relative p-6 rounded-2xl
        bg-forest-card/80 backdrop-blur-sm
        border border-forest-light/20
        transition-all duration-300
        ${accentGlows[accentColor]}
      `} data-id="element-14">
      <div className="flex items-start justify-between mb-4" data-id="element-15">
        {videoUrl ? <div className="w-12 h-12 rounded-xl overflow-hidden bg-forest/60 flex-shrink-0" data-id="element-16">
            <video src={videoUrl} autoPlay loop muted playsInline className="w-full h-full object-cover" data-id="element-17" />
          </div> : iconUrl ? <img src={iconUrl} alt={name} className="w-10 h-10 object-contain" data-id="element-18" /> : <span className="text-4xl" data-id="element-19">{icon}</span>}
        <span className={`px-3 py-1 text-xs font-medium rounded-full border ${statusStyles[status]}`} data-id="element-20">
          {status}
        </span>
      </div>

      <h3 className="text-xl font-semibold text-cream mb-2" data-id="element-21">{name}</h3>
      <p className="text-parchment text-sm leading-relaxed mb-3" data-id="element-22">
        {description}
      </p>

      {tags && tags.length > 0 && <div className="flex flex-wrap gap-1.5 mb-4" data-id="element-23">
          {tags.map(tag => <span key={tag} className="px-2 py-0.5 text-[10px] font-medium tracking-wide uppercase rounded-md bg-forest-light/15 text-parchment/80 border border-forest-light/15" data-id="element-24">
              {tag}
            </span>)}
        </div>}

      {link && status === 'Live' && <a href={link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm font-medium text-flame hover:text-amber transition-colors duration-200" data-id="element-25">
          View App
          <ExternalLinkIcon className="w-4 h-4" data-id="element-26" />
        </a>}

      {status !== 'Live' && <span className="text-sm text-parchment/60 italic" data-id="element-27">
          {status === 'In Progress' ? 'Building...' : 'Stay tuned'}
        </span>}
    </motion.div>;
}